import PropTypes from "prop-types";
import { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { IconCheck, IconCopy, IconKey } from "@tabler/icons";
import ActionButton from "src/components/shared/ActionButton";
import { copyText } from "./api";
import { receivedWords } from "./decisions";

// A code the customer sent from the official site (France-Visas emails it
// to them) in the last hour: at the top of the case, big enough to read off
// the screen, until someone marks it used (or ticks a filing step) or the
// hour is over. Older codes are in the timeline.
export default function CodeStrip({ code, now, canEdit, onUsed }) {
  const [copied, setCopied] = useState(false);
  const long = String(code.text).length > 24;
  return (
    <Box
      component="section"
      aria-label="Code from the customer"
      sx={{
        mb: 2,
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: 2,
        borderColor: "warning.main",
        bgcolor: (t) => alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.16 : 0.1),
        display: "flex",
        alignItems: "center",
        columnGap: 2,
        rowGap: 1.5,
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
        <Typography variant="body2" fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <IconKey size={16} aria-hidden />
          The customer sent a code · {receivedWords(code.sent_at, now)}
        </Typography>
        <Typography
          sx={{
            fontFamily: "monospace",
            fontWeight: 700,
            fontSize: long ? "1.125rem" : { xs: "1.75rem", sm: "2.125rem" },
            letterSpacing: long ? 0 : "0.08em",
            lineHeight: 1.25,
            mt: 0.5,
            overflowWrap: "anywhere",
            whiteSpace: long ? "pre-wrap" : "normal",
          }}
        >
          {code.text}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
        <ActionButton
          variant="contained"
          startIcon={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
          onClick={async () => {
            if (await copyText(String(code.text))) {
              setCopied(true);
              setTimeout(() => setCopied(false), 1400);
            }
          }}
          aria-label={copied ? "Copied" : "Copy the code"}
        >
          {copied ? "Copied" : "Copy"}
        </ActionButton>
        {canEdit ? (
          <ActionButton variant="outlined" busyLabel="Saving…" onClick={onUsed}>
            Mark as used
          </ActionButton>
        ) : null}
      </Stack>
    </Box>
  );
}

CodeStrip.propTypes = {
  code: PropTypes.shape({ text: PropTypes.string.isRequired, sent_at: PropTypes.string }).isRequired,
  now: PropTypes.number.isRequired,
  canEdit: PropTypes.bool,
  onUsed: PropTypes.func.isRequired,
};
